import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { academyAssign } from '../../../services/api';
import { getUsers } from '../../../services/api';

/**
 * Admin → assign a published course to selected employees. Sends the
 * assignment fan-out to the backend which also fires per-user push +
 * email notifications via the existing Portal helpers.
 */
export default function CourseAssign() {
  const { courseId, version } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const all = await getUsers();
        setUsers(all.filter(u => ['employee', 'subcontractor'].includes(u.role)));
      } catch (_) {
        toast.error('Could not load users');
      }
    })();
  }, []);

  const toggle = (id) => {
    const s = new Set(picked);
    s.has(id) ? s.delete(id) : s.add(id);
    setPicked(s);
  };

  const submit = async () => {
    if (picked.size === 0) return;
    setBusy(true);
    try {
      const res = await academyAssign(courseId, version, {
        user_ids: Array.from(picked),
        target_completion_at: target || null,
      });
      toast.success(`Assigned to ${res.assigned} employee(s)`);
      navigate('/admin/academy');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="academy-assign">
      <h1 className="text-2xl font-display font-bold flex items-center gap-2">
        <Users className="h-5 w-5" /> Assign to employees
      </h1>
      <p className="text-sm text-slate-600">
        Course: <b>{courseId}</b> v{version}. Assigning fires push + email
        notifications automatically.
      </p>
      <label className="text-sm block">
        Target completion date (optional):
        <input type="date" value={target} onChange={(e) => setTarget(e.target.value)}
          className="ml-2 border rounded px-2 py-1" data-testid="academy-assign-target" />
      </label>
      <div className="border rounded max-h-96 overflow-y-auto divide-y">
        {users.map((u) => (
          <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={picked.has(u.id)} onChange={() => toggle(u.id)}
              data-testid={`assign-check-${u.id}`} />
            <div>
              <div className="font-medium">{u.name}</div>
              <div className="text-xs text-slate-500">{u.email} · {u.role}</div>
            </div>
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <Button onClick={submit} disabled={busy || picked.size === 0} data-testid="academy-assign-submit">
          Assign to {picked.size} employee{picked.size === 1 ? '' : 's'}
        </Button>
        <Button variant="ghost" onClick={() => navigate('/admin/academy')}>Cancel</Button>
      </div>
    </div>
  );
}
